import React, { useState, useEffect, useCallback } from "react";
import "./SotGa4MappingTool.scss";
import { FullPageSpinner, Page } from "../Components";
import { useTabbedNav } from "../utils/hooks/useNav";
import { Router, RouteComponentProps } from "@reach/router";
import { useNavigationGuard } from "../utils/hooks/useNavigationGuard";
import AlertDialog, { AlertDialogVariants } from "../Components/AlertDialog/AlertDialog";
import { MdSmsFailed } from "react-icons/md";
import { SubBrand1 } from "../utils/colors";
import SotGa4Labeling from "./SotGa4Labeling";
import {
  CommittedValue,
  RequiredSegmentNames,
  SystemGeneratedSegmentNames,
} from "../SegmentationMapping/CustomSegments";
import { awaitJSON, SegmentationMappingLambdaFetch } from "../utils/fetch-utils";
import { useCompanyInfo } from "../redux/company";
import { useSetError } from "../redux/modals";
import pako from "pako";
import { base64ToUint8Array } from "./SotGa4MappingUtils";

export interface CustomSegmentsData {
  segmentId: string;
  segmentName: string;
  values: CommittedValue[];
}

export interface SotGa4MappingRow {
  campaign_key: string;
  ingested_seg_id: string;
  pg_seg_id: string;
  platform_id: string;
  channel_id: string;
  raw_source: string;
  raw_medium: string;
  campaign_name: string;
  campaign_id: string;
  ad_group_name: string;
  ad_group_id: string;
  ad_name: string;
  ad_id: string;
  channel: string;
  platform: string;
  [key: string]: string;
}

export const dataFreshnessDataHeaders = [
  {
    name: "company",
    label: "Company",
    flex: 1,
  },
  {
    name: "source",
    label: "Source",
    flex: 1,
  },
  {
    name: "account_id",
    label: "Account ID",
    flex: 1,
  },
  {
    name: "account_name",
    label: "Account Name",
    flex: 1,
  },
  {
    name: "table_name",
    label: "Table Name",
    flex: 1,
  },
  {
    name: "last_updated",
    label: "Last Updated",
    flex: 1,
  },
];

const enum PageTab {
  LABELING = "labeling",
}

const NAVS = [{ label: "Labeling", key: PageTab.LABELING }];

const SotGa4MappingTool: React.FC = ({ navigate }: RouteComponentProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const { tab, goToTab } = useTabbedNav({
    navigate,
    baseURL: "sot-ga4-mapping-tool",
    defaultKey: PageTab.LABELING,
    navs: NAVS.map(nav => nav.key, NAVS),
  });
  const { cid } = useCompanyInfo();
  const setError = useSetError();

  useEffect(() => {
    if (!tab) {
      goToTab(PageTab.LABELING);
    }
  }, [tab, goToTab]);

  const {
    attemptNavigate: handleTabClick,
    confirmNavigation: confirmTabNavigation,
    cancelNavigation: cancelTabNavigation,
    showAlert: showAlertModal,
  } = useNavigationGuard({
    hasUnsavedChanges,
    onConfirmNavigate: key => {
      goToTab(key);
      setHasUnsavedChanges(false);
    },
  });

  const [customSegments, setCustomSegments] = useState<CustomSegmentsData[] | undefined>();
  const [rawData, setRawData] = useState<SotGa4MappingRow[] | null>(null);

  const fetchRawData = useCallback(async () => {
    try {
      const response = await SegmentationMappingLambdaFetch("/getRawAnalyticsData", {
        params: { company: cid },
      });

      let responseText = await response.text();

      let responseStringBase64: string;
      try {
        responseStringBase64 = JSON.parse(responseText);
      } catch {
        responseStringBase64 = responseText;
      }

      const compressedData = base64ToUint8Array(responseStringBase64);
      const unCompressedJsonString = pako.ungzip(compressedData, { to: "string" });

      const data = JSON.parse(unCompressedJsonString);

      setRawData(data as SotGa4MappingRow[]);
    } catch (error) {
      console.error("Fetch error", error);
      setError({ message: "Failed to fetch data", reportError: error });
    }
  }, [cid, setError]);

  useEffect(() => {
    if (!rawData) {
      fetchRawData();
    }
  }, [cid, setError, rawData, fetchRawData]);

  useEffect(() => {
    if (!customSegments) {
      (async () => {
        try {
          setLoading(true);
          let res = await SegmentationMappingLambdaFetch("/getCustomSegments", {
            params: { company: cid },
          });
          const data = await awaitJSON(res);

          data.sort((a, b) => {
            // Ordering should be:
            // 1st - Channel
            // 2nd - Platform
            // 3rd - Campaign Ownership
            // 4th - Include in Fee Calc
            // Then all remaining values in alphabetical order.
            const segmentNameToRelativeValue = segName => {
              switch (segName) {
                case SystemGeneratedSegmentNames.CHANNEL:
                  return 1;
                case SystemGeneratedSegmentNames.PLATFORM:
                  return 2;
                case RequiredSegmentNames.CAMPAIGN_OWNERSHIP:
                  return 3;
                case RequiredSegmentNames.INCLUDE_IN_FEE_CALC:
                  return 4;
                default:
                  return 99;
              }
            };

            if (
              segmentNameToRelativeValue(a.segmentName) < segmentNameToRelativeValue(b.segmentName)
            ) {
              return -1;
            } else if (
              segmentNameToRelativeValue(a.segmentName) > segmentNameToRelativeValue(b.segmentName)
            ) {
              return 1;
            }

            return a.segmentName.localeCompare(b.segmentName);
          });

          data.forEach(seg => {
            seg.values.sort((a, b) => {
              if (a.valueName === "N/A") {
                return -1;
              } else if (b.valueName === "N/A") {
                return 1;
              }

              return a.valueName.localeCompare(b.valueName);
            });
          });

          setCustomSegments(data);
        } catch (e) {
          const reportError = e as Error;
          setError({ message: reportError.message, reportError });
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [cid, customSegments, setError]);

  return (
    <Page
      app2Redesign
      title="SOT/GA4 Mapping Tool"
      pageType="SOT/GA4 Mapping Tool"
      navs={NAVS}
      selectedNav={tab}
      onNav={handleTabClick}
    >
      <div className="segmentationMappingPage">
        {loading && <FullPageSpinner />}
        <Router className="fullPageRouter">
          <SotGa4Labeling
            data={rawData}
            customSegments={customSegments}
            fetchRawData={fetchRawData}
            path={PageTab.LABELING}
          />
        </Router>
      </div>
      <AlertDialog
        title="Are you sure you want to leave?"
        body="If you leave now, your changes will not be saved."
        icon={<MdSmsFailed color={SubBrand1} size={75} />}
        variant={AlertDialogVariants.ERROR}
        show={showAlertModal}
        primaryButtonLabel="Stay"
        secondaryButtonLabel="Leave"
        onHide={cancelTabNavigation}
        secondaryAction={confirmTabNavigation}
      />
    </Page>
  );
};

export default SotGa4MappingTool;
